/**
 * UNSENT WORK: WHAT A RELOAD OR A CLOSE WOULD THROW AWAY (forms plan §2.2, D8).
 *
 * The owner chose to PREVENT the loss rather than keep drafts (D8 (a)): nothing
 * typed is stored in the browser. Instead, a form or an editor that holds text,
 * edits or a typed reason the server has not taken says so here while it holds
 * it, and three things read the registry before they throw anything away:
 *
 *   - the service worker's reload (lib/pwa.ts defers it, and the version toast
 *     names what is waiting: "reload after you send or discard: VPN policy");
 *   - the browser's leave guard (`beforeunload`, armed only while the registry
 *     holds something, so a clean tab keeps the back-forward cache);
 *   - the page's Escape rule (features/soc/SocRoute.tsx, which also reads the
 *     `[data-unsaved-draft]` mark close9 introduced; Form writes that mark too).
 *
 * WHAT COUNTS. An entry is registered by `useUnsentWork(key, dirty, describe)`
 * while `dirty` is true and dropped when it turns false or the component
 * unmounts. A surface that marks its draft in the DOM instead
 * (`data-unsaved-draft`, the playbook editor) is counted too, unless the mark
 * says "false" or the surface is shut (`aria-hidden`), so the two mechanisms
 * never disagree about whether something would be lost.
 */
import { useEffect } from "react";

export interface UnsentEntry {
  /** Who holds it: "vpn-policy", "decoy-policy:web-02". Unique per holder. */
  key: string;
  /** What would be lost, in words: "VPN policy (edited)". */
  describe: string;
}

const registry = new Map<string, string>();
const listeners = new Set<() => void>();

function notify(): void {
  syncLeaveGuard();
  for (const fn of [...listeners]) fn();
}

/** Register `key` as holding unsent work; the returned function drops it. */
export function registerUnsent(key: string, describe: string): () => void {
  registry.set(key, describe);
  notify();
  return () => {
    // Only drop what this registration put there: a remount under the same key
    // may already have re-registered.
    if (registry.get(key) === describe) {
      registry.delete(key);
      notify();
    }
  };
}

/** A draft a surface marks in the DOM, in front (not under a shut surface). */
function markedDrafts(): UnsentEntry[] {
  if (typeof document === "undefined") return [];
  const out: UnsentEntry[] = [];
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-unsaved-draft]"))) {
    const mark = el.getAttribute("data-unsaved-draft");
    if (mark === "false") continue;
    if (el.closest('[aria-hidden="true"]')) continue;
    // A kit form registers itself and also writes the mark; count it once.
    const key = el.getAttribute("data-unsent-key");
    if (key && registry.has(key)) continue;
    out.push({
      key: key || el.getAttribute("data-panel") || el.getAttribute("data-testid") || "draft",
      describe: el.getAttribute("data-unsent-describe") || el.getAttribute("aria-label") || "a draft not saved"
    });
  }
  return out;
}

/**
 * Text typed into an open confirm that no form registered: a reason in a
 * dialog is work too (the version toast has always held its reload for one).
 */
function typedInOpenDialogs(): UnsentEntry[] {
  if (typeof document === "undefined") return [];
  const fields = document.querySelectorAll<HTMLTextAreaElement | HTMLInputElement>(
    '[role="dialog"] textarea, [role="dialog"] input[type="text"], [aria-modal="true"] textarea, dialog[open] textarea, dialog[open] input[type="text"]'
  );
  const typed = Array.from(fields).some((f) => f.value.trim() !== "" && !f.closest("[data-unsent-key]"));
  return typed ? [{ key: "open-dialog", describe: "a reason typed in an open form" }] : [];
}

/** Everything that would be lost now: registered first, then the DOM's marks, then typed dialog text. */
export function hasUnsentWork(): UnsentEntry[] {
  const registered = [...registry].map(([key, describe]) => ({ key, describe }));
  return [...registered, ...markedDrafts(), ...typedInOpenDialogs()];
}

/** The list in words, for a toast or a confirm: "VPN policy (edited) and a reason typed". */
export function unsentWorkWords(entries: UnsentEntry[] = hasUnsentWork()): string {
  const words = [...new Set(entries.map((e) => e.describe))];
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/** Called whenever the registry changes. Returns the unsubscribe. */
export function subscribeUnsent(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Register while `dirty`. `describe` names the work ("VPN policy (edited)");
 * keep it short, it is read in a toast. Nothing is stored anywhere.
 */
export function useUnsentWork(key: string, dirty: boolean, describe: string): void {
  useEffect(() => {
    if (!dirty || !key) return undefined;
    return registerUnsent(key, describe);
  }, [key, dirty, describe]);
}

/* ── the leave guard ─────────────────────────────────────────────────────
 * Armed only while the registry holds something: a `beforeunload` listener on
 * a clean tab costs the back-forward cache in some browsers for nothing. */
let guardArmed = false;

function onBeforeUnload(event: BeforeUnloadEvent): void {
  if (registry.size === 0) return;
  event.preventDefault();
  // The legacy spelling some browsers still need before they show the prompt.
  event.returnValue = "";
}

function syncLeaveGuard(): void {
  if (typeof window === "undefined") return;
  const want = registry.size > 0;
  if (want && !guardArmed) {
    window.addEventListener("beforeunload", onBeforeUnload);
    guardArmed = true;
  } else if (!want && guardArmed) {
    window.removeEventListener("beforeunload", onBeforeUnload);
    guardArmed = false;
  }
}

/** Test seam: whether the leave guard is armed now. */
export function leaveGuardArmed(): boolean {
  return guardArmed;
}

/** Test seam: forget every registration (each test starts clean). */
export function resetUnsentWorkForTests(): void {
  registry.clear();
  notify();
}
